import React, { useEffect, useMemo, useRef, useState } from "react";
import { withLatestOperationalData } from "@utils/latestOperationalData";
import { isEqual } from "lodash";
import { t } from "ttag";
import { Schedule as ScheduleOffIcon } from "@material-ui/icons";
import { useStoreActions } from "@models/RootStore";
import { useSetModbusUnitRegisterValue } from "@hooks/useModbusDevicesApi";
import { IModbusDeviceRow, UI_TYPE_BINARY, UI_TYPE_REVERSE_BINARY } from "../../../Modbus/types";
import { ModbusUnitControl, getModbusRegisterKey } from "../../../Modbus/ModbusUnitControl";
import type { IUITemplate } from "../../../Modbus/UITemplateMapper";
import ModbusUIRenderer from "./ModbusUIRenderer";
import { IconButton, useToast } from "@coolremote/shared";
import useUniversalUnitShape from "@hooks/useUniversalUnitShape";
import useTypes from "@hooks/useTypes";
import UnitPanel from "../UnitPanel/UnitPanel";
import { isModbusPowerMeterFallback } from "../ModbusSchedule/constants";
import { useStyles } from "./styles";

const DEBOUNCE_MS = 600;
/**
 * Universal properties a "powerMeter" entity exposes. Read from types at
 * runtime; this is the documented fallback (see ModbusEntityType) for when
 * the types store hasn't loaded yet.
 */
const POWER_METER_PROPERTIES = ["power", "energy"];
/** How long an unconfirmed optimistic value keeps overriding incoming data. */
const PENDING_TTL_MS = 15000;

// Canonical UI-template design space — matches UITemplateMapper's editor
// canvas exactly, so a template renders at the same visual proportions here.
const CANVAS_WIDTH = 486;
const CANVAS_HEIGHT = 495;

interface IModbusUnitEntry {
  id: string;
  name: string;
  rows: IModbusDeviceRow[];
  isConnected?: boolean;
  operationalData?: Record<string, any>;
  uiTemplate?: Record<string, any>;
  entityTemplate?: boolean;
  entityType?: string;
  schedules?: string[];
  activeSchedulesCount?: number;
  paramTemplateData?: {
    entityTemplate?: boolean;
    entityType?: string;
    rows?: IModbusDeviceRow[];
  };
}

interface IModbusControlPanelProps {
  entry: IModbusUnitEntry;
  isCelsius?: boolean;
  tempSymbol?: string;
  siteTemp?: { C: number | null; F: number | null };
  navigateToSchedules?: () => void;
}

const ModbusControlPanel: React.FC<IModbusControlPanelProps> = ({
  entry,
  isCelsius,
  tempSymbol,
  siteTemp,
  navigateToSchedules,
}) => {
  const { id, name, rows } = entry;
  // Start from what arrived over the websocket since the entry was fetched, not just the entry itself
  const entryOperationalData: Record<string, any> = useMemo(
    () => withLatestOperationalData(id, entry.operationalData ?? {}),
    [id, entry.operationalData]
  );
  const classes = useStyles();

  // Computed server-side from the same schedule documents used to build
  // `schedules`, so no need to fetch the full schedules list just for a count.
  const activeSchedulesCount = entry.activeSchedulesCount ?? 0;
  const { flashError } = useToast();
  const types: any = useTypes();
  const [operationalData, setOperationalData] = useState<Record<string, any>>(entryOperationalData);

  const {
    synthesizedUnit,
    isUniversalUnit,
    hasUniversalControls,
    unitPanelStoreProps,
    handleSetActiveOperationStatus,
    handleSetActiveSetpoint,
    handleSetActiveOperationMode,
    handleSetActiveFanMode,
    handleSetActiveSwingMode,
    handleNavigateToSchedules,
  } = useUniversalUnitShape({
    entry,
    rows,
    navigateToSchedules,
    isCelsius,
    protocol: "modbus",
  });

  // Optimistic values still awaiting confirmation from the device, keyed by
  // register. A push or refetch that lands before the device reports back
  // still carries the old value, so overlay these to stop controls bouncing.
  const pendingValues = useRef<Record<string, number>>({});
  const pendingExpiry = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const applyPending = (incoming: Record<string, any>): Record<string, any> => {
    const pending = pendingValues.current;
    const keys = Object.keys(pending);
    if (!keys.length) return incoming;
    const merged = { ...incoming };
    keys.forEach((key) => {
      if (incoming[key] === pending[key]) {
        clearTimeout(pendingExpiry.current[key]);
        delete pendingExpiry.current[key];
        delete pending[key];
      } else {
        merged[key] = pending[key];
      }
    });
    return merged;
  };

  // Sync from entry when the parent re-fetches (mirrors BACnet's useBacnetUniversalControls pattern).
  useEffect(() => {
    const next = applyPending(entryOperationalData);
    if (!isEqual(operationalData, next)) {
      setOperationalData(next);
    }
  }, [entry.operationalData]);
  const addWebSocketListener = useStoreActions((a) => a.addWebSocketListener);
  const removeWebSocketListener = useStoreActions((a) => a.removeWebSocketListener);
  const { mutate: setValue } = useSetModbusUnitRegisterValue({ onError: flashError });

  // Scale the fixed-size canvas to fill the panel's width, the same way
  // BACnet's BacnetControlPanel scales its scalableMappingArea.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [canvasScale, setCanvasScale] = useState(1);

  useEffect(() => {
    const bodyEl = bodyRef.current;
    if (!bodyEl) return undefined;
    const updateScale = () => {
      // Keep the last good scale on a zero-width measurement (panel not laid
      // out yet / hidden) — scaling to 0 collapses the canvas entirely.
      const width = bodyEl.offsetWidth;
      if (!width) return;
      setCanvasScale(width / CANVAS_WIDTH);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(bodyEl);
    return () => observer.disconnect();
  }, []);

  // Per-row debounce timers keyed by row name.
  const pendingTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    const handle = (message: any) => {
      if (message.name !== "UPDATE_MODBUS_UNIT_DATA" || message.data?.unitId !== id) return;
      const newData = message.data?.operationalData;
      if (!newData) return;
      setOperationalData((prev) => {
        // The device pushes full snapshots; a partial message (an optimistic value) only updates its own points
        const next = applyPending(message.data?.partial ? { ...prev, ...newData } : newData);
        return isEqual(prev, next) ? prev : next;
      });
    };
    addWebSocketListener(handle);
    return () => removeWebSocketListener(handle);
  }, [id]);

  // Clear all pending timers on unmount.
  useEffect(() => {
    return () => {
      Object.values(pendingTimers.current).forEach(clearTimeout);
      Object.values(pendingExpiry.current).forEach(clearTimeout);
    };
  }, []);

  const handleSetValue = (row: IModbusDeviceRow, _rowIndex: number, value: number) => {
    // Optimistic update — instant visual feedback using same key format as operationalData.
    const key = getModbusRegisterKey(row);
    if (key) {
      setOperationalData((prev) => ({ ...prev, [key]: value }));
      pendingValues.current[key] = value;
      // Stop pinning the optimistic value if the device never confirms, so a
      // dropped write can't leave the control stuck showing the wrong state.
      clearTimeout(pendingExpiry.current[key]);
      pendingExpiry.current[key] = setTimeout(() => {
        delete pendingValues.current[key];
        delete pendingExpiry.current[key];
      }, PENDING_TTL_MS);
    }

    const send = () =>
      setValue({
        modbusUnitId: id,
        data: {
          register: {
            type: row.registerType,
            address: row.registerAddress,
            ...(row.bitNumber != null ? { bitNumber: row.bitNumber } : {}),
          },
          value,
        },
      });

    if (pendingTimers.current[row.name]) {
      clearTimeout(pendingTimers.current[row.name]);
    }

    // A toggle is a single discrete action — send it right away. Debouncing is
    // only useful for steppers, where rapid +/- clicks collapse into one write.
    if (row.uiType === UI_TYPE_BINARY || row.uiType === UI_TYPE_REVERSE_BINARY) {
      send();
      return;
    }

    pendingTimers.current[row.name] = setTimeout(() => {
      delete pendingTimers.current[row.name];
      send();
    }, DEBOUNCE_MS);
  };

  // Only an AC ("controlUnit") universal unit gets the HVAC control screen — a
  // power meter's universal properties are just readings, so it lists those
  // instead. `entityType` only exists from CM-118 onward; universal units saved
  // before it were always AC units, hence a missing type counts as AC.
  const entityType = entry.paramTemplateData?.entityType ?? entry.entityType;
  const isPowerMeter = isUniversalUnit && entityType === "powerMeter";
  const isUniversalAcUnit = isUniversalUnit && !isPowerMeter;
  const powerMeterProperties = Object.keys(
    types?.universalEntityPropertiesByEntityType?.powerMeter ?? {},
  );
  const meterReadingRows = rows.filter(
    (row) =>
      !!row.property &&
      (powerMeterProperties.length ? powerMeterProperties : POWER_METER_PROPERTIES).includes(row.property),
  );

  // A universal AC unit with no UI template falls back to the standard HVAC
  // control screen, showing only the controls whose registers were mapped.
  // Non-universal units keep the raw register list.
  const showUniversalFallback = !entry.uiTemplate && isUniversalAcUnit;

  // Power meters can't create/edit schedules (see isModbusPowerMeterFallback);
  // only show the header button when there's an existing schedule to view/delete.
  const hideScheduleButtonForPowerMeter =
    isModbusPowerMeterFallback(entry) && !((entry?.schedules?.length ?? 0) > 0);

  const renderBodyContent = () => {
    if (entry.uiTemplate) {
      return (
        <div
          className={classes.canvasScaler}
          style={{ transform: `scale(${canvasScale})` }}
        >
          <ModbusUIRenderer
            uiTemplate={entry.uiTemplate as unknown as IUITemplate}
            rows={rows}
            operationalData={operationalData}
            onSetValue={handleSetValue}
          />
        </div>
      );
    }

    if (isPowerMeter) {
      if (!meterReadingRows.length) {
        return (
          <div className={classes.emptyState}>
            {t`No power or energy parameters mapped for this meter.`}
          </div>
        );
      }
      return (
        <ModbusUnitControl
          unitName={name}
          rows={meterReadingRows}
          operationalData={operationalData}
          readOnly
        />
      );
    }

    if (isUniversalAcUnit) {
      if (hasUniversalControls && synthesizedUnit) {
        return (
          <UnitPanel
            {...unitPanelStoreProps}
            unit={synthesizedUnit}
            isCelsius={isCelsius}
            tempSymbol={tempSymbol}
            siteTemp={siteTemp}
            setActiveOperationStatus={handleSetActiveOperationStatus}
            setActiveSetpoint={handleSetActiveSetpoint}
            setActiveOperationMode={handleSetActiveOperationMode}
            setActiveFanMode={handleSetActiveFanMode}
            setActiveSwingMode={handleSetActiveSwingMode}
            navigateToSchedules={handleNavigateToSchedules}
            activeSchedulesCountOverride={activeSchedulesCount}
          />
        );
      }
      return (
        <div className={classes.emptyState}>
          {t`No universal controls configured for this unit.`}
        </div>
      );
    }

    return (
      <ModbusUnitControl
        unitName={name}
        rows={rows}
        operationalData={operationalData}
        onSetValue={handleSetValue}
      />
    );
  };

  const handleSchedulesClick = () => {
    if (navigateToSchedules) {
      navigateToSchedules();
    }
  };

  return (
    <div className={classes.panel}>
      {/* UnitPanel renders its own name, so the panel header would duplicate it. */}
      {!showUniversalFallback && (
        <div className={classes.header}>
          <span className={classes.unitName}>{name}</span>
          {navigateToSchedules && !hideScheduleButtonForPowerMeter && (
            <div className={classes.scheduleButtonWrapper}>
              <IconButton
                onClick={handleSchedulesClick}
                className={classes.scheduleButton}
                title="Manage Schedules"
              >
                <ScheduleOffIcon fontSize="medium" />
                {activeSchedulesCount > 0 && (
                  <span className={classes.badge}>{activeSchedulesCount}</span>
                )}
              </IconButton>
            </div>
          )}
        </div>
      )}
      <div
        className={classes.body}
        ref={bodyRef}
        style={entry.uiTemplate ? { height: CANVAS_HEIGHT * (canvasScale || 1) } : undefined}
      >
        {renderBodyContent()}
      </div>
    </div>
  );
};

export default ModbusControlPanel;
